import React, { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { MdDownload, MdInfoOutline } from 'react-icons/md';
import { getData } from '../../Webservice/Webservice';

const toCsv = (columns, rows) => {
  const header = columns.map((c) => `"${c.label}"`).join(',');
  const body = rows
    .map((row) => columns.map((c) => `"${String(row[c.key] ?? '').replace(/"/g, '""')}"`).join(','))
    .join('\n');
  return `${header}\n${body}`;
};

const downloadCsv = (filename, csv) => {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const ReportTable = ({ title, columns, rows }) => (
  <Paper sx={{ p: 2, borderRadius: '12px', mb: 3 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography>
      <Button
        size="small"
        startIcon={<MdDownload />}
        onClick={() => downloadCsv(`${title.replace(/\s+/g, '_')}.csv`, toCsv(columns, rows))}
        disabled={!rows.length}
      >
        Export CSV
      </Button>
    </Box>
    <TableContainer sx={{ maxHeight: 480 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {columns.map((c) => (
              <TableCell key={c.key} sx={{ fontWeight: 700 }}>{c.label}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={columns.length} sx={{ textAlign: 'center', color: '#94A3B8', py: 4 }}>
                No data for the selected filters.
              </TableCell>
            </TableRow>
          )}
          {rows.map((row, idx) => (
            <TableRow key={idx} hover>
              {columns.map((c) => {
                const value = row[c.key];
                const isDateField = /date|At$/i.test(c.key) && value && !isNaN(Date.parse(value));
                const isProfitField = c.key === 'profit' && typeof value === 'number';

                let display = value ?? '-';
                if (c.key === 'pdfUrl' || (row.pdfUrl && c.key === 'invoiceNumber' && row.pdfUrl)) {
                  display = row[c.key];
                } else if (isDateField) {
                  display = new Date(value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
                } else {
                  display = String(value ?? '-');
                }

                return (
                  <TableCell
                    key={c.key}
                    sx={isProfitField ? { color: value >= 0 ? '#16a34a' : '#dc2626', fontWeight: 600 } : undefined}
                  >
                    {isProfitField ? `${value >= 0 ? '+' : ''}₹${value}` : display}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  </Paper>
);

const GenericReportPage = () => {
  const { pathname } = useLocation();
  const reportKey = pathname.split('/reports/')[1];

  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [notesOpen, setNotesOpen] = useState(false);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const tokenValue = sessionStorage.getItem('token');
      const token = tokenValue ? `Bearer ${tokenValue}` : '';
      const params = {};
      if (from) params.from = from;
      if (to) params.to = to;
      const res = await getData(`reports/${reportKey}`, params, token, false);
      if (res && res.success) {
        setData(res);
      } else {
        setError(res?.message || 'Failed to load report');
      }
    } catch (err) {
      setError(err.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportKey, from, to]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ fontWeight: 800, mb: 2 }}>
        {data?.title || 'Report'}
      </Typography>

      <Paper sx={{ p: 2, borderRadius: '12px', mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField
              label="From"
              type="date"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="To"
              type="date"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Button variant="contained" onClick={fetchReport} disabled={loading}>
              Apply Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!loading && !error && data && (
        <>
          {Array.isArray(data.notes) && data.notes.length > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <Button variant="outlined" size="small" startIcon={<MdInfoOutline />} onClick={() => setNotesOpen(true)}>
                Note
              </Button>
            </Box>
          )}

          <Dialog open={notesOpen} onClose={() => setNotesOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 700 }}>Note</DialogTitle>
            <DialogContent dividers>
              <Box component="ul" sx={{ pl: 2, m: 0 }}>
                {(data.notes || []).map((note, i) => (
                  <Typography component="li" key={i} variant="body2" sx={{ mb: 1 }}>{note}</Typography>
                ))}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setNotesOpen(false)}>Close</Button>
            </DialogActions>
          </Dialog>

          {Array.isArray(data.summary) && data.summary.length > 0 && (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {data.summary.map((card, i) => (
                <Grid item xs={12} sm={6} md={3} key={i}>
                  <Paper sx={{ p: 2, borderRadius: '12px' }}>
                    <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 700 }}>
                      {card.label}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>{card.value}</Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}

          <ReportTable title={data.title} columns={data.columns || []} rows={data.rows || []} />

          {Array.isArray(data.extraTables) && data.extraTables.map((t, i) => (
            <ReportTable key={i} title={t.title} columns={t.columns} rows={t.rows} />
          ))}
        </>
      )}
    </Box>
  );
};

export default GenericReportPage;
